/* ---------------------------------------------------------------------------
   Where a value came from, field by field.

   `GR-20`, and what survives of the struck `GR-14`. Every record in this
   register now has one provenance as a RECORD — the synthetic placeholders are
   gone — so the thing worth knowing is no longer which record to trust, it is
   which FIELD. Three sources sit side by side on each of the five, and until
   this module the application drew all three in the same typeface with no mark
   between them:

     · Spans, lengths, widths, deck level, HFL, superstructure — read off
       as-built General Arrangement and cross-section drawings. The register's
       strongest data. Quote it.
     · `lat` / `lng` — the pins of Google Maps links supplied on 2026-08-17.
       Enough to put a marker on a map and find the structure. NOT a surveyed
       position: nothing has been checked against a control point, an abutment
       centreline, or the chainage the same record carries.
     · `deckBearing` — the axis of the OpenStreetMap `bridge=yes` way at the
       crossing, queried once through Overpass on the same day. Cartography
       rather than inventory, accurate to a degree or two of what a volunteer
       mapper drew.

   All of that is already written down, in `seed.ts`'s header and in the
   README's provenance table. None of it reached the screen, which is the gap
   this closes.

   TWO RULES.

   **The table describes the SEEDED records and nothing else.** A record that
   arrived through the CSV import or was created in the app has whatever
   provenance its spreadsheet had, which this project does not know. Claiming
   "as-built drawing" for it because the field name matches would be inventing
   provenance, which is worse than showing none. So a record whose id is not in
   `BRIDGES` reports `unattributed` for everything, and the record says so.

   **A corrected value outranks its origin.** Coordinates and the deck axis are
   editable — by design, on the map, by dragging — and `GR-07` records who moved
   them and when. A `lat` somebody who knows the crossing has put right is
   better data than the pin it replaced, and marking it as a Google Maps pin
   would be a lie the register has the evidence to avoid. So where the change log
   has been loaded and shows a field was edited, that is the provenance.

   NOT A DATA-MODEL CHANGE, deliberately. No column, no migration, no
   `source` field per value — the roadmap sized this as a marker and a legend and
   that is what it is. If provenance ever needs to be captured per value at write
   time, that is a different and much larger item.
   --------------------------------------------------------------------------- */

import type { Bridge, BridgeChange } from './types.ts';
import { BRIDGES } from './seed.ts';
import { BRIDGES_DPWH } from './dpwh.ts';
/* TEMPORARY — demonstration data for FWO. Delete with src/data/fwo-demo.ts. */
import { FWO_DEMO_BRIDGES, isDemoField } from './fwo-demo.ts';
import { RBI_FIELDS, rbiFor } from './dpwh-rbi.ts';

/**
 * How a value got into the register, worst-known to best.
 *
 * `unattributed` is not the bottom of the scale — it is off it. A value nobody
 * recorded the source of might be excellent or invented, and the honest report
 * is that the register does not know.
 */
export type SourceTier =
  | 'none'
  | 'unattributed'
  | 'cartography'
  | 'pin'
  | 'inventory'
  | 'listing'
  | 'drawing'
  | 'corrected'
  /* The load restriction's own two (LC-23): written by an accepted rating, or
     set by hand through the inventory form. */
  | 'rating'
  | 'manual'
  /* Written by an accepted handover (LC-11): the as-built model's own value,
     or one the resident engineer entered in its place. */
  | 'model'
  | 'handover'
  /* Entered at an accepted works completion in place of its model's value (LC-82). */
  | 'works'
  /* TEMPORARY. Not a rank on this scale at all — it is off the bottom of it, the
     way `unattributed` is off the bottom in the other direction. See
     DEMONSTRATION below and the header of src/data/fwo-demo.ts. */
  | 'demonstration';

export interface FieldProvenance {
  tier: SourceTier;
  /** The short mark drawn beside a value. Empty for the baseline. */
  mark: string;
  /** What the mark stands for, for the legend and the tooltip. */
  label: string;
  /** One sentence on how far to trust it. */
  note: string;
}

/* --------------------------- the tiers --------------------------- */

const DRAWING: FieldProvenance = {
  tier: 'drawing',
  /* No mark. Marking the baseline as well as the exceptions marks nothing: the
     point of a marked value is that it is not like the ones around it. The
     legend says plainly that unmarked means drawing. */
  mark: '',
  label: 'As-built drawing',
  note: 'Transcribed from the as-built general-arrangement or cross-section drawing. The register’s strongest data.',
};

const PIN: FieldProvenance = {
  tier: 'pin',
  mark: 'P',
  label: 'Map pin',
  note: 'Read off a Google Maps link, 2026-08-17. Good enough to find the structure; not a surveyed position, and not checked against a control point or the chainage on this record.',
};

const CARTOGRAPHY: FieldProvenance = {
  tier: 'cartography',
  mark: 'O',
  label: 'OpenStreetMap',
  note: 'The axis of the OSM bridge way at this crossing, queried once through Overpass on 2026-08-17. Cartography, not inventory — accurate to a degree or two of what a volunteer mapper drew.',
};

/**
 * DPWH's Road & Bridge Inventory, reached through OpenStreetMap.
 *
 * Ranked above `pin` and below `drawing`. Better than a pin because it is the
 * asset owner's own inventory joined on the asset owner's own bridge id, and
 * corroborated five ways — see the header of src/data/dpwh-rbi.ts. Worse than a
 * drawing because the position is a volunteer's tracing of the structure rather
 * than a measured centreline, and because it reached this register through a
 * third party rather than from DPWH directly.
 */
const INVENTORY_IMPORT: FieldProvenance = {
  tier: 'inventory',
  mark: 'R',
  label: 'DPWH inventory, via OpenStreetMap',
  note:
    'From DPWH’s own Road & Bridge Inventory, matched on this record’s bridge id through ' +
    'OpenStreetMap (Overpass, 2026-09-02) and cross-checked against the station, the length ' +
    'and the District Engineering Office. Good enough to find the crossing; the position is a ' +
    'traced way centre, not a surveyed point.',
};

/**
 * The market-sounding pack this register was built from.
 *
 * Ranked below the inventory: it is a real DPWH document, and it is a
 * pre-procurement listing rather than an asset record. It is the source of
 * record for the fields it carries — name, package, station, length, lanes —
 * and it is why the register exists at all.
 */
const LISTING: FieldProvenance = {
  tier: 'listing',
  mark: 'L',
  label: 'Market-sounding listing',
  note:
    'From the DPWH Daang Maharlika Road Improvement Project market-sounding pack, March 2026. ' +
    'A pre-procurement listing, not a survey and not an asset record: no structure in it has ' +
    'been inspected, and the condition it shows against each is a desk assessment.',
};

const UNATTRIBUTED: FieldProvenance = {
  tier: 'unattributed',
  mark: '?',
  label: 'Source not recorded',
  note: 'This record did not come from a drawing held in this project, and the register does not record where its values came from.',
};

/**
 * TEMPORARY — the FWO demonstration register. Delete with src/data/fwo-demo.ts.
 *
 * NOT A SOURCE, and deliberately worse than `unattributed`. An unattributed
 * value might be excellent and simply unsourced; this one is known to be
 * invented. It is here because the alternative — letting fabricated records
 * report `unattributed` like any imported spreadsheet — would make made-up
 * numbers indistinguishable from numbers whose origin was merely not written
 * down, on the one screen a reader takes away on paper.
 *
 * The mark is `D` and it is drawn beside EVERY value on those records, not just
 * the weak ones, because on a demonstration record there are no strong ones.
 */
const DEMONSTRATION: FieldProvenance = {
  tier: 'demonstration',
  mark: 'D',
  label: 'Demonstration data',
  note: 'For demonstration only. Not measured, not surveyed, not transcribed from any document — see the header of src/data/fwo-demo.ts. Nothing on this record may be quoted.',
};

const NONE: FieldProvenance = {
  tier: 'none',
  mark: '',
  label: 'Not recorded',
  note: 'The source document does not carry this field.',
};

/**
 * The fields whose source is weaker than the drawing the rest came off.
 *
 * Listed rather than derived, and short on purpose: these are exactly the three
 * `seed.ts` singles out in its header. Everything else on a seeded record came
 * off the drawing, so the table only has to name the exceptions.
 */
const WEAKER: Record<string, FieldProvenance> = {
  lat: PIN,
  lng: PIN,
  deckBearing: CARTOGRAPHY,
};

/** Whether this record is one of the five this project holds the drawings for. */
export function isSeeded(bridgeId: string): boolean {
  return BRIDGES.some((b) => b.id === bridgeId);
}

const DPWH_IDS = new Set(BRIDGES_DPWH.map((b) => b.id));

/* TEMPORARY. The ids of the fabricated FWO demonstration records. Built from the
   array itself rather than written out, so a record added there cannot be missed
   here and quietly report a provenance it does not have. Delete with
   src/data/fwo-demo.ts. */
const DEMO_IDS = new Set(FWO_DEMO_BRIDGES.map((b) => b.id));

/**
 * The provenance of one field on one seeded record, before corrections.
 *
 * PER REGISTER, because provenance is a property of a source document and the
 * departments do not share one. Nepal's five came off as-built drawings with
 * three weaker fields called out; DPWH's 108 came off a market-sounding listing
 * with inventory values joined onto 43 of them. A record in neither seed is
 * `unattributed`, which is the honest answer for anything imported through the
 * CSV path or created in the app.
 *
 * Adding a third department means adding a branch here. That is deliberate: a
 * default would silently attribute somebody's spreadsheet to a drawing.
 */
function seedProvenance(b: Bridge, field: keyof Bridge): FieldProvenance {
  /* TEMPORARY, AND FIRST ON PURPOSE. A fabricated record must not fall through
     to any of the branches below, and it must not reach `unattributed` either —
     see DEMONSTRATION. Delete with src/data/fwo-demo.ts. */
  if (DEMO_IDS.has(b.id)) return DEMONSTRATION;
  /* And a DPWH record's field the demonstration filled in (src/data/dpwh-demo.ts). */
  if (isDemoField(b.id, field as string)) return DEMONSTRATION;

  if (isSeeded(b.id)) return WEAKER[field as string] ?? DRAWING;

  if (DPWH_IDS.has(b.id)) {
    /* Only where the inventory actually reached this field on THIS record. A
       record with no match, or a field the inventory did not carry, is the
       listing's — or nothing, which `provenanceOf` has already returned. */
    const inventory = (RBI_FIELDS as readonly string[]).includes(field as string);
    return inventory && rbiFor(b.id) !== null ? INVENTORY_IMPORT : LISTING;
  }

  return UNATTRIBUTED;
}

/** A value the accepted handover read from the as-built model (LC-11). */
export function modelProvenance(modelId: string | null, completedOn: string, at = 'handover'): FieldProvenance {
  return {
    tier: 'model',
    mark: 'M',
    label: 'As-built model',
    note: `Read from the as-built model${modelId ? ` ${modelId}` : ''} at ${at} on ${completedOn}.`,
  };
}

/* --------------------------- the lookup --------------------------- */

/**
 * Where one field's current value came from.
 *
 * `changes` is the record's change log where it has been loaded, and null where
 * it has not — which is the ordinary state, since the history panel fetches on
 * demand. Null means *not known to have been edited*, not *never edited*, and
 * this reports the seeded origin in that case rather than claiming either way.
 * The legend says so.
 */
export function provenanceOf(
  b: Bridge,
  field: keyof Bridge,
  changes: readonly BridgeChange[] | null = null,
): FieldProvenance {
  const value = b[field];
  if (value === null || value === undefined || value === '') return NONE;

  /* THE POSTING SAYS WHERE IT CAME FROM (LC-23), and outranks everything below:
     an accepted rating wrote it, or somebody set it by hand, and either way the
     register recorded which — even on a demonstration record. */
  if (field === 'loadRestriction' && b.posting) {
    const when = b.posting.at ? b.posting.at.slice(0, 10) : 'a date not recorded';
    const who = b.posting.by ?? 'somebody not attributable';
    return b.posting.source === 'rating'
      ? {
          tier: 'rating',
          mark: 'RT',
          label: 'Accepted load rating',
          note: `Written by accepting rating ${b.posting.ratingId ?? '—'}, the bridge's governing rating, on ${when} by ${who}.`,
        }
      : {
          tier: 'manual',
          mark: '✎',
          label: 'Set by hand',
          note: `Set through the inventory form on ${when} by ${who}, not by a rating.`,
        };
  }

  /* A correction outranks the origin, and carries who made it. Only `edit` and
     `placement` touch a field's value; an `inspection` row is a workflow
     transition and moves nothing on the record. A `handover` row is the
     handover writing it, and says whether the value was the model's. */
  const edit = (changes ?? [])
    .filter((c) => c.field === field && c.source !== 'inspection')
    .reduce<BridgeChange | null>((latest, c) => (latest && latest.changedAt >= c.changedAt ? latest : c), null);

  /* TEMPORARY — an invented handover (the FWO demonstration's overpass) filled
     its fields from an invented model, so they stay `D` rather than claiming
     `M`. Delete the `DEMO_IDS` term with src/data/fwo-demo.ts. */
  /* Filled from a model in the inventory form and saved as it states them (LC-83). */
  if (edit?.source === 'model') {
    return {
      tier: 'model',
      mark: 'M',
      label: 'Read from a model',
      note: `Filled from model ${edit.ref ?? 'not named'} on ${edit.changedAt.slice(0, 10)}${edit.author ? ` by ${edit.author}` : ''}.`,
    };
  }

  /* The latest works completion (LC-82), which came after any handover. */
  const w = b.worksCompleted ?? null;
  const atWorks = w ? w.modelFields.concat(w.enteredFields) : [];
  if (edit?.source === 'works' || (edit === null && atWorks.includes(field as never))) {
    if (w && w.modelFields.includes(field as never)) return modelProvenance(w.modelId, w.completedOn, 'works completion');
    return {
      tier: 'works',
      mark: 'W',
      label: 'Entered at works completion',
      note: `Entered at the works completion${w ? ` of ${w.completedOn}` : ''}${edit?.author ? ` by ${edit.author}` : ''}.`,
    };
  }

  const atHandover = DEMO_IDS.has(b.id) ? [] : (b.handover?.modelFields ?? []).concat(b.handover?.enteredFields ?? []);
  if (edit?.source === 'handover' || (edit === null && atHandover.includes(field as never))) {
    const h = b.handover;
    if (h && h.modelFields.includes(field as never)) return modelProvenance(h.modelId, h.completedOn);
    return {
      tier: 'handover',
      mark: 'H',
      label: 'Entered at handover',
      note: `Entered at handover${edit?.author ? ` by ${edit.author}` : ''} in place of the as-built model's value.`,
    };
  }

  if (edit) {
    return {
      tier: 'corrected',
      mark: '✎',
      label: 'Corrected in this register',
      note:
        `Set to this value on ${edit.changedAt.slice(0, 10)}` +
        (edit.author ? ` by ${edit.author}` : ' — Access was not enforced, so not attributable') +
        `. Before that it read ${edit.oldValue === null ? 'nothing' : `"${edit.oldValue}"`}.`,
    };
  }

  return seedProvenance(b, field);
}

/**
 * The distinct provenances present on one record, for its legend.
 *
 * Only what is actually on the page: a legend listing tiers the reader cannot
 * see any of is noise, and the one thing it must always carry is the line
 * explaining that unmarked means the drawing.
 */
export function legendFor(
  b: Bridge,
  fields: readonly (keyof Bridge)[],
  changes: readonly BridgeChange[] | null = null,
): FieldProvenance[] {
  const seen = new Map<SourceTier, FieldProvenance>();
  for (const field of fields) {
    const p = provenanceOf(b, field, changes);
    if (p.tier === 'none') continue;
    if (!seen.has(p.tier)) seen.set(p.tier, p);
  }
  return [...seen.values()].sort((a, b2) => a.label.localeCompare(b2.label));
}
